import AurServerSingleSelect from './AurServerSingleSelect';
import {t} from 'i18next';
import {Option} from '../aur-select/AurSelect';
import {useEffect} from 'react';
import {useState} from 'react';
import {getClientsList} from '../../../api/ClientManagementService';
import {AxiosResponse} from 'axios';

type AurClientSelectProps = {
  field: {
    value: string | undefined;
    label: string | undefined;
    onChange: (selectedOption: Option) => void;
    onBlur?: () => void;
  };
  fieldState: {
    error?: {
      message?: string;
    };
  };
  setInitialLoading?: (value: boolean) => void;
  pageLoadSize?: number;
  isAdminOnlyView?: boolean | undefined;
  isUserViewOnlyScreen?: boolean | undefined;
  assignNewClient?: boolean | undefined;
};

const AurClientSelect = (props: AurClientSelectProps) => {
  const {pageLoadSize = 50} = props;

  const [clientItems, setClientItems] = useState<Option[]>([]);
  const [clientHasNextPage, setClientHasNextPage] = useState<boolean>(true);
  const [clientIsLoading, setClientIsLoading] = useState<boolean>(false);

  const fetchClients = async (
    startIndex: number,
    stopIndex: number,
    searchTerm?: string
  ) => {
    const params = [];

    // Page
    const pageSize = stopIndex - startIndex;
    const pageNumber = stopIndex / pageSize;

    props.isAdminOnlyView &&
      params.push(`IsAdminOnlyView=${props.isAdminOnlyView}`);
    props.isUserViewOnlyScreen &&
      params.push(`isUserViewOnlyScreen=${props.isUserViewOnlyScreen}`);
    props.assignNewClient &&
      params.push(`assignNewClient=${props.assignNewClient}`);
    params.push(`PageNumber=${pageNumber}`);
    params.push(`PageSize=${pageSize}`);
    searchTerm && params.push(`SearchTerm=${searchTerm}`);

    // Build the query string
    let queryParams = '';
    if (params.length > 0) {
      queryParams = `?${params.join('&')}`;
    }
    try {
      setClientIsLoading(true);
      return await getClientsList(queryParams);
    } finally {
      setClientIsLoading(false);
    }
  };

  const fetchInitialClients = async () => {
    try {
      props.setInitialLoading && props.setInitialLoading(true);
      const result = await fetchClients(0, pageLoadSize);
      setClientResultsHasNextPage(result);
      setClientItems(clientResultsToOptions(result));
    } catch (e) {
      console.error('Error', e);
    } finally {
      props.setInitialLoading && props.setInitialLoading(false);
    }
  };

  const fetchMoreClients = async (startIndex: number, stopIndex: number) => {
    try {
      const result = await fetchClients(startIndex, stopIndex);
      setClientResultsHasNextPage(result);
      return clientResultsToOptions(result);
    } catch (e) {
      console.error('Error', e);
    }
  };

  const searchClients = async (searchTerm: string) => {
    try {
      const result = await fetchClients(0, pageLoadSize, searchTerm);
      return clientResultsToOptions(result);
    } catch (e) {
      console.error('Error', e);
    }
  };

  const clientResultsToOptions = (result: AxiosResponse<any, any>) => {
    return result?.data?.data?.map((client: any) => {
      return {
        key: client.clientId,
        value: client.clientId,
        label: client.name,
        budgetConsumptionEnabled: client.budgetConsumptionEnabled,
      };
    });
  };

  const setClientResultsHasNextPage = (result: AxiosResponse<any, any>) => {
    const totalPages = result?.data?.meta?.pagination?.totalPages;
    const currentPage = result?.data?.meta?.pagination?.pageNumber;
    const hasNextPage = currentPage < totalPages;
    setClientHasNextPage(hasNextPage);
  };

  useEffect(() => {
    fetchInitialClients();
  }, []);

  return (
    <AurServerSingleSelect
      optionList={clientItems}
      itemsPerPage={pageLoadSize}
      onPage={fetchMoreClients}
      hasNextPage={clientHasNextPage}
      searchable
      isLoading={clientIsLoading}
      onSearch={searchClients}
      helperText=""
      dataTestId="aur-test-client-dropdown"
      label={t('addEditTeamMember.form.client')}
      aria-label={t('addEditTeamMember.form.client')}
      labelId={'client'}
      field={{
        ...props.field,
        label: props.field.label,
      }}
      fieldState={props.fieldState}
    />
  );
};

export default AurClientSelect;
